import { Link } from '@tanstack/react-router'
import { createContext, Fragment, useContext } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { Icon } from '@/components/ui/icon'
import { cn } from '@/lib/utils'

/** One step of the trail. The last step is the page itself. */
export type Crumb = { label: string; to?: string }

/**
 * Where the app shell's header keeps the page's trail. The shell provides
 * the element; a page that has a trail of its own portals it there, and the
 * shell's own trail (section › group › page, from the sidebar's map) stands
 * down — see `data-page-trail` below and the `group-has-*` in `AppShell`.
 * Without a shell (a page mounted alone in a test) the trail renders in place.
 */
const HeaderTrailContext = createContext<HTMLElement | null>(null)
export const HeaderTrailProvider = HeaderTrailContext.Provider

/**
 * The trail, one line: earlier steps muted and linked where they lead
 * somewhere, the page itself in foreground. On a phone only the page shows —
 * the whole trail would wrap the header.
 */
export function Breadcrumbs({
  trail,
  className,
  page = false,
}: {
  trail: readonly Crumb[]
  className?: string
  /** A page's own trail, which takes the header's place when there is one. */
  page?: boolean
}) {
  const { t } = useTranslation()
  const slot = useContext(HeaderTrailContext)

  const nav = (
    <nav
      aria-label={t('common.page.breadcrumb')}
      data-page-trail={page ? '' : undefined}
      className={cn('min-w-0', className)}
    >
      <ol className="flex min-w-0 items-center gap-1 text-sm">
        {trail.map((step, index) => {
          const last = index === trail.length - 1
          return (
            <Fragment key={`${step.label}-${String(index)}`}>
              {index > 0 ? (
                <li aria-hidden="true" className="hidden shrink-0 sm:flex">
                  <Icon name="chevron_right" size={16} className="text-muted-foreground" />
                </li>
              ) : null}
              <li
                aria-current={last ? 'page' : undefined}
                className={cn(
                  'min-w-0 truncate',
                  last ? 'text-foreground font-medium' : 'text-muted-foreground hidden sm:block',
                )}
              >
                {step.to && !last ? (
                  <Link to={step.to} className="hover:text-foreground rounded-sm transition-colors">
                    {step.label}
                  </Link>
                ) : (
                  step.label
                )}
              </li>
            </Fragment>
          )
        })}
      </ol>
    </nav>
  )

  return page && slot ? createPortal(nav, slot) : nav
}
